import type { MillsheetFieldsFragment } from '@mbfab/client-api'
import type { ShapeNameConversionDisplayMap } from '@mbfab/shared'
import { convertShapeNameForDisplay, isSome } from '@mbfab/shared'
import dayjs from 'dayjs'
import { sum, uniq } from 'es-toolkit'

export type UnmatchedMillsheetItemViewModel = {
  id: string
  // サイズ・長さは変換マスタの適用結果（v1 UnlinkedMillsheetsTable の
  // convertShapeNameForDisplay と同じ。照合には使わず表示のみを変換する）
  size: string | null
  length: string | null
  quantity: number
  weight: number | null
  heatNumber: string
}

export type UnmatchedMillsheetViewModel = {
  id: string
  tenantId: string
  no: string
  orderNumber: string
  constructionName: string
  tradingCompany: string
  manufacturerName: string
  // 明細の集約列（v1 UnlinkedMillsheetsTable と同じ: uniq + 空落とし + カンマ区切り）
  productNames: string
  steelTypes: string
  totalWeight: number
  issuedDate: string
  items: UnmatchedMillsheetItemViewModel[]
}

function uniqueValues(values: Array<string | null | undefined>): string {
  return uniq(values.filter(isSome))
    .filter((value) => value !== '')
    .join(', ')
}

export function toUnmatchedMillsheetViewModel(
  fragment: MillsheetFieldsFragment,
  shapeNameConversionMap: ShapeNameConversionDisplayMap,
): UnmatchedMillsheetViewModel {
  return {
    id: fragment.id,
    tenantId: fragment.tenantId,
    no: fragment.no,
    orderNumber: fragment.orderNumber,
    constructionName: fragment.constructionName,
    tradingCompany: fragment.tradingCompany,
    manufacturerName: fragment.manufacturerName,
    productNames: uniqueValues(
      fragment.millsheetItems.map((item) =>
        convertShapeNameForDisplay(shapeNameConversionMap, 'productName', item.sectionType),
      ),
    ),
    steelTypes: uniqueValues(fragment.millsheetItems.map((item) => item.steelType)),
    totalWeight: sum(fragment.millsheetItems.map((item) => item.weightBeforeProcessing).filter(isSome)),
    // importedAt と同じ表記（YYYY/MM/DD）に揃える（v1 と同じ）
    issuedDate: dayjs(fragment.issuedDate).format('YYYY/MM/DD'),
    items: fragment.millsheetItems.map((item) => ({
      id: item.id,
      size: convertShapeNameForDisplay(shapeNameConversionMap, 'size', item.size),
      length: convertShapeNameForDisplay(shapeNameConversionMap, 'length', item.length),
      quantity: item.quantity,
      weight: item.weightBeforeProcessing,
      heatNumber: item.heatNumber,
    })),
  }
}

// 注文番号の保存結果。v1 のモーダルは同じ3分岐をトーストで示す（紐付いた / 空にした / 該当する発注が無いまま保存）。
// v2 はトーストに頼らず DOM に残る形で示すため、呼び出し側（unmatched-mill-sheet-row.tsx）が
// この結果を feedback として描画する
export type UpdateOrderNumberResult =
  | { status: 'linked' }
  | { status: 'clearedUnlinked' }
  | { status: 'savedUnmatched' }
  | { status: 'error'; message: string }
